import React, { useEffect, useMemo, useState } from 'react'
import {Map, AdvancedMarker, useMap} from '@vis.gl/react-google-maps'
import { Truck, MapPinHouse } from 'lucide-react'
import './style.scss'

const POLL_MS = 20000

const FitMapToMarkers = ({ driverLocation, destination }) => {
    const map = useMap()

    useEffect(() => {
        if (!map || !driverLocation || !destination || !window.google) return

        const bounds = new window.google.maps.LatLngBounds()
        bounds.extend(driverLocation)
        bounds.extend(destination)

        map.fitBounds(bounds, {
            top: 36,
            right: 36,
            bottom: 36,
            left: 36,
        })

        // Prevent over-zooming when driver and destination are very close
        const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
            if (map.getZoom() > 17) {
                map.setZoom(17)
            }
        })

        return () => {
            window.google.maps.event.removeListener(listener)
        }
    }, [map, driverLocation, destination])

    return null
}

const DeliveryLiveMap = ({
    deliveryId,
    destination,
    initialDriverLocation = null,
    fetchDriverLocation,
 }) => {
    const [driverLocation, setDriverLocation] = useState(initialDriverLocation)

    const hasDestination =
        Number.isFinite(destination?.lat) &&
        Number.isFinite(destination?.lng)

    const hasDriver =
        Number.isFinite(driverLocation?.lat) &&
        Number.isFinite(driverLocation?.lng)

    const center = useMemo(() => {
        if (hasDriver) return driverLocation
        if (hasDestination) return destination
        return { lat: 25.2048, lng: 55.2708 }
    }, [hasDriver, hasDestination, driverLocation, destination])

    useEffect(() => {
        if (!deliveryId || !fetchDriverLocation || !hasDestination) return undefined

        let cancelled = false

        const load = async () => {
            try {
                const nextLocation = await fetchDriverLocation(deliveryId)

                const lat = Number(nextLocation?.lat)
                const lng = Number(nextLocation?.lng)

                if (!cancelled && Number.isFinite(lat) && Number.isFinite(lng)) {
                    setDriverLocation({
                        lat,
                        lng,
                    })
                } else if (!cancelled) {
                    setDriverLocation(null)
                }
            } catch (error) {
                console.error('Error fetching driver location', error)

                if (!cancelled) {
                    setDriverLocation(null)
                }
            }
        }

        load()

        const interval = window.setInterval(load, POLL_MS)

        return () => {
            cancelled = true
            window.clearInterval(interval)
        }
    }, [deliveryId, fetchDriverLocation, hasDestination])

    if (!hasDestination || !hasDriver) return null

    return (
        <div className="DeliveryMap">
            <Map
                className="DeliveryMap__map"
                defaultCenter={center}
                defaultZoom={14}
                mapId={process.env.REACT_APP_GOOGLE_MAPS_MAP_ID}
                gestureHandling="greedy"
                disableDefaultUI
            >
                {hasDriver ? (
                    <AdvancedMarker position={driverLocation} title="Driver location" zIndex={20}>
                        <div className="MapMarker MapMarker--driver" aria-label="Driver location">
                            <Truck size={19} strokeWidth={2.5} />
                        </div>
                    </AdvancedMarker>
                ) : null}
                <FitMapToMarkers
                    driverLocation={driverLocation}
                    destination={destination}
                />
                <AdvancedMarker position={destination} title="Delivery address" zIndex={10}>
                    <div className="MapMarker MapMarker--destination" aria-label="Delivery address">
                        <MapPinHouse size={21} strokeWidth={2.4} />
                    </div>
                </AdvancedMarker>
            </Map>
        </div>
    )
}

export default DeliveryLiveMap
